import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthCredentials {
  password: string;
}

interface BasicAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: AuthCredentials) => boolean;
  logout: () => void;
}

const BasicAuthContext = createContext<BasicAuthContextType | undefined>(undefined);

export const useBasicAuth = () => {
  const context = useContext(BasicAuthContext);
  if (context === undefined) {
    throw new Error('useBasicAuth must be used within a BasicAuthProvider');
  }
  return context;
};

interface BasicAuthProviderProps {
  children: ReactNode;
}

export const BasicAuthProvider: React.FC<BasicAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = localStorage.getItem('basicAuth');
    if (authStatus === 'authenticated') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const login = (credentials: AuthCredentials): boolean => {
    const { password } = credentials;
    
    // Get password from environment variable
    const expectedPassword = import.meta.env.VITE_BASIC_AUTH_PASSWORD || 'caprone';
    
    // Check if password matches
    if (password === expectedPassword) {
      setIsAuthenticated(true);
      localStorage.setItem('basicAuth', 'authenticated');
      return true;
    }
    
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('basicAuth');
  };

  const value = {
    isAuthenticated,
    isLoading,
    login,
    logout
  };

  return (
    <BasicAuthContext.Provider value={value}>
      {children}
    </BasicAuthContext.Provider>
  );
}; 